import React, { ComponentProps } from 'react'
import * as Yup from 'yup'
import { Fragment, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationCircleIcon, ExclamationIcon, PlusCircleIcon } from '@heroicons/react/outline'
import clsx from 'clsx'
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { Place } from '../types/utils'
import GoogleSearch from './GoogleSearch'

const schema = Yup.object().shape({
  place: Yup.object()
    .shape({
      googlePlaceId: Yup.string(),
      description: Yup.string(),
    })
    .required(),
})

type NewPlaceTDO = {
  place?: Pick<Place, 'googlePlaceId' | 'id'>
}

const initialValues: NewPlaceTDO = {
  place: undefined,
}

type NewLocationBtnProps = {
  nbRemainingLicenses: number
  claimFn: (place: Pick<Place, 'googlePlaceId' | 'id'>) => void
} & ComponentProps<'button'>

const NewLocationBtn: React.FC<NewLocationBtnProps> = (props) => {
  const { children, onClick, nbRemainingLicenses, claimFn, ...rest } = props
  const [open, setOpen] = useState(false)

  const cancelButtonRef = useRef(null)

  const handleOnSubmit = async (values: NewPlaceTDO, formikBag: FormikHelpers<NewPlaceTDO>) => {
    if (!values.place) {
      formikBag.setFieldError('place', 'Please add at least one location.')
      return
    }
    if (values.place) {
      claimFn(values.place)
      setOpen(false)
    }
  }

  return (
    <Fragment>
      <button onClick={() => setOpen(true)} {...rest}>
        {children}
      </button>
      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={setOpen}
        >
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={schema}
                  validateOnChange={false}
                  validateOnBlur={false}
                  onSubmit={handleOnSubmit}
                >
                  {({ setFieldValue, isSubmitting, values, errors }: FormikProps<NewPlaceTDO>) => {
                    return (
                      <Form>
                        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                          <div className="sm:flex sm:items-start">
                            <div
                              className={clsx({
                                'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10':
                                  true,
                                'bg-green-100': nbRemainingLicenses > 0,
                                'bg-yellow-100': nbRemainingLicenses === 0,
                              })}
                            >
                              {nbRemainingLicenses > 0 ? (
                                <PlusCircleIcon className="h-6 w-6 text-green-800" aria-hidden="true" />
                              ) : (
                                <ExclamationIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                              )}
                            </div>
                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                              <Dialog.Title as="h3" className="page-title">
                                {nbRemainingLicenses > 0 ? 'Claim a new location' : 'No licences available'}
                              </Dialog.Title>

                              <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                  {nbRemainingLicenses > 0
                                    ? 'Your request will be sent to Buzzjar to review. Once approved you will be able to manage this location. This location will count against your number of licences available.'
                                    : 'You don’t have any available licences. Please contact Buzzjar to discuss your options.'}
                                </p>

                                {nbRemainingLicenses > 0 && (
                                  <>
                                    <fieldset disabled={isSubmitting} className="mt-7">
                                      <Field
                                        name="place"
                                        component={GoogleSearch}
                                        menuPlacement="bottom"
                                        label="Location Name"
                                        single
                                        handleSelect={(selected: Place) => {
                                          setFieldValue('place', selected)
                                        }}
                                      />
                                      {errors.place ? (
                                        <div className="mt-2 flex items-center">
                                          <div className="pointer-events-none inset-y-0 flex items-center pr-1">
                                            <ExclamationCircleIcon
                                              className="h-5 w-5 text-red-400"
                                              aria-hidden="true"
                                            />
                                          </div>
                                          <p className="text-xs text-red-900">{errors.place}</p>
                                        </div>
                                      ) : null}
                                    </fieldset>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                          <button
                            type="submit"
                            className={clsx({
                              'btn inline-flex w-full justify-center sm:ml-3 sm:w-auto': true,
                              'btn-delete': !nbRemainingLicenses,
                              'btn-primary': nbRemainingLicenses,
                            })}
                            // onClick={handleAction}
                          >
                            {nbRemainingLicenses > 0 ? 'Submit Request' : 'Done'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-white mt-3 inline-flex w-full justify-center sm:mt-0 sm:ml-3 sm:w-auto"
                            onClick={() => setOpen(false)}
                            ref={cancelButtonRef}
                          >
                            Cancel
                          </button>
                        </div>
                      </Form>
                    )
                  }}
                </Formik>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </Fragment>
  )
}

export default NewLocationBtn
